import {
    getDomainFromUrl,
    ACTIVE_SCREEN_KEY,
    setScreenData,
    formatMessage,
    setErrorScreenMessage,
    setSuccessfulScreenMessage,
    hideDrawer,
} from 'cw-ui-core';
import { call, put, fork, cancel } from 'redux-saga/effects';
import { GET, DELETE, POST, PUT, getHeaders, autoHideMessages, loader, PATCHv2 } from 'src/services/ApiService';
import Locale from 'src/i18n';
import { EDIT_PRODUCT_DRAWER } from 'src/features/users/constants/drawerConstants';

export const SoftwareCatalog = Object.freeze({
    '35d537be-b6a7-11e8-a7b7-0800273596d2': 'Automate',
    'c921c0e4-9b47-480a-bc84-82a1077c2c10': 'Control',
    '4e8117dd-6c2d-4385-a1c9-2b821e1aa14e': 'Manage',
    '814a1adf-ce6b-4a29-a391-032cb4f01323': 'Sell',
});

export const getSoftwareIdClaim = claims => claims?.find(claim => claim.type === 'software_id')?.value;

export const getInstanceName = (uri, softwareId) => {
    const hostname = uri ? getDomainFromUrl(uri, false) : '--';
    if (SoftwareCatalog[softwareId] === 'Manage') {
        const companyPattern = /(.*auth\/)(.*)(\/CentralLoginAcs.*)/;
        const company = uri ? uri.replace(companyPattern, '$2') : '--';
        return hostname === '--' ? hostname : `${hostname} | ${company}`;
    }
    return hostname;
};

export function* getToken(screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, '/RegistrationTokens', getHeaders());
    if (result.isSuccess) {
        yield put(setScreenData(['token'], result.response.registrationToken, screenKey));
        yield call(autoHideMessages);
    } else {
        const error = formatMessage(Locale.failedGetRegistrationToken);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages, screenKey);
    }
}

const compareScopes = (a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();

    let comparison = 0;
    if (nameA > nameB) {
        comparison = 1;
    } else if (nameA < nameB) {
        comparison = -1;
    }
    return comparison;
};

export function* loadProducts(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isUserProductsReady'], false));
    const result = yield call(GET, '/clients', getHeaders());
    if (result.isSuccess) {
        yield put(setScreenData(['products'], result.response, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetProducts);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
    }
    yield put(setScreenData(['isUserProductsReady'], true));
}

export function* loadClients(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isProductsReady'], false));
    const result = yield call(GET, '/clients', getHeaders());
    if (result.isSuccess) {
        const instances = [];

        result.response.forEach(client => {
            const updatedClient = client;
            updatedClient.product = SoftwareCatalog[updatedClient.softwareId]
                ? SoftwareCatalog[updatedClient.softwareId]
                : '--';
            updatedClient.instanceName = getInstanceName(updatedClient.redirectUris[0], updatedClient.softwareId);
            instances.push(updatedClient);
        });

        yield put(setScreenData(['clients'], instances, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetProducts);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
    }
    yield put(setScreenData(['isProductsReady'], true));
}

export function getAllowedGrantTypes() {
    return ['authorization_code', 'implicit', 'hybrid', 'client_credentials'];
}

export function* getProductInstance(id, screenKey = ACTIVE_SCREEN_KEY) {
    const apiResourceResult = yield call(GET, '/apiResources', getHeaders());
    if (apiResourceResult.isSuccess) {
        yield put(setScreenData(['apiResourceCategories'], apiResourceResult.response.sort(compareScopes), screenKey));
    } else {
        const error = formatMessage(Locale.failedAuthorizationEndpoints);
        yield put(setErrorScreenMessage(error, false, apiResourceResult.errorMessages, screenKey));
        return;
    }

    const identityResourceResult = yield call(GET, '/identityResources', getHeaders());
    if (identityResourceResult.isSuccess) {
        yield put(setScreenData(['identityResources'], identityResourceResult.response.sort(compareScopes), screenKey));
    } else {
        const error = formatMessage(Locale.failedAuthorizationEndpoints);
        yield put(setErrorScreenMessage(error, false, identityResourceResult.errorMessages, screenKey));
    }

    const result = yield call(GET, `/clients/${id}`, getHeaders());
    if (result.isSuccess) {
        const apiScopes = apiResourceResult.response.map(x => x.scopes).flat();
        result.response.apiScopes = result.response.allowedScopes.filter(scope =>
            apiScopes.some(s => s.name === scope)
        );
        result.response.identityScopes = result.response.allowedScopes.filter(scope =>
            identityResourceResult.response.some(s => s.name === scope)
        );
        const softwareId = getSoftwareIdClaim(result.response.claims);
        result.response.instance = getInstanceName(result.response.redirectUris[0], softwareId);
        result.response.softwareId = softwareId;
        result.response.productName = SoftwareCatalog[softwareId];
        yield put(setScreenData(['instanceDetails'], result.response, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetProductInstance);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
    const resultUsers = yield call(GET, `/clients/${id}/users`, getHeaders());
    if (resultUsers.isSuccess) {
        const users = resultUsers.response.map(user => user.userId);
        yield put(setScreenData(['checkboxes', 'selectedUsers'], users, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetProductInstanceUsers);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
}

export function* editProductInstance(id, productInstanceUpdates, users, secrets) {
    let productInstanceResult;
    let usersResult;
    const errors = [];

    if (productInstanceUpdates) {
        productInstanceResult = yield call(PATCHv2, `/clients/${id}`, getHeaders(), productInstanceUpdates);
    }
    if (users) {
        const newUsers =
            users.map(user => {
                return { userId: user };
            }) ?? [];
        usersResult = yield call(PUT, `/clients/${id}/users`, getHeaders(), newUsers);
    }
    if (secrets) {
        const newSecrets = secrets
            .filter(secret => !secret.markDelete && secret.id == null)
            .map(secret => ({
                type: secret.type,
                value: secret.value,
                description: secret.description,
            }));

        const deleteSecrets = secrets.filter(secret => secret.markDelete && secret.id != null);

        for (let i = 0; i < newSecrets.length; i += 1) {
            const secret = newSecrets[i];
            const result = yield call(POST, `/clients/${id}/secrets`, getHeaders(), secret);
            if (result && !result.isSuccess) {
                errors.push({
                    screenMessage: formatMessage(Locale.failedUpdateProductInstance),
                    errorMessages: result.errorMessages,
                });
            }
        }

        for (let i = 0; i < deleteSecrets.length; i += 1) {
            const secret = deleteSecrets[i];
            const result = yield call(DELETE, `/clients/${id}/secrets/${secret.id}`, getHeaders());
            if (result && !result.isSuccess) {
                errors.push({
                    screenMessage: formatMessage(Locale.failedUpdateProductInstance),
                    errorMessages: result.errorMessages,
                });
            }
        }
    }

    if (productInstanceResult && !productInstanceResult.isSuccess) {
        errors.push({
            screenMessage: formatMessage(Locale.failedUpdateProductInstance),
            errorMessages: productInstanceResult.errorMessages,
        });
    }
    if (usersResult && !usersResult.isSuccess) {
        errors.push({
            screenMessage: formatMessage(Locale.failedUpdateProductInstanceUsers),
            errorMessages: usersResult.errorMessages,
        });
    }

    if (errors.length > 0) {
        for (let i = 0; i < errors.length; i += 1) {
            yield put(
                setErrorScreenMessage(errors[i].screenMessage, false, errors[i].errorMessages, EDIT_PRODUCT_DRAWER)
            );
            yield call(autoHideMessages, EDIT_PRODUCT_DRAWER);
        }
    } else {
        yield call(loadClients);
        yield put(hideDrawer(EDIT_PRODUCT_DRAWER));
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.instanceEdit), false, []));
        yield call(autoHideMessages);
    }
}

export function* deleteProductInstance(id) {
    const result = yield call(DELETE, `/clients/${id}`, getHeaders());
    if (result.isSuccess) {
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.productInstanceDeletedSuccessful), false, []));
        yield call(loadClients);
        yield call(autoHideMessages);
    } else {
        const error = formatMessage(Locale.productInstanceDeletedFailed);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield call(autoHideMessages);
    }
}

export function* addOrRemoveUsersFromApplication(id, selectedUserIds, selectedOption, screenKey = ACTIVE_SCREEN_KEY) {
    const timeOut = yield fork(loader);
    let users = [];
    const resultUsers = yield call(GET, `/clients/${id}/users`, getHeaders());
    if (resultUsers.isSuccess) {
        users = resultUsers.response.map(x => ({ userId: x.userId }));
        switch (selectedOption) {
            case 'addApplication':
                users.push(...selectedUserIds.map(userId => ({ userId })));
                break;
            case 'removeApplication':
                users = users.filter(user => !selectedUserIds.includes(user.userId));
                break;
            default:
                break;
        }
        const result = yield call(PUT, `/clients/${id}/users`, getHeaders(), users);
        if (result.isSuccess) {
            yield cancel(timeOut);
            yield put(setSuccessfulScreenMessage(formatMessage(Locale.applicationAccessUpdateSuccessful), false, []));
            yield call(autoHideMessages);
        } else {
            yield cancel(timeOut);
            yield put(setErrorScreenMessage(formatMessage(Locale.applicationAccessUpdateFailed), false, [], screenKey));
            yield call(autoHideMessages);
        }
    } else {
        const error = formatMessage(Locale.failedGetRoleUsers);
        yield put(setErrorScreenMessage(error, false, resultUsers.errorMessages, screenKey));
        yield call(autoHideMessages);
    }
}
